import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import BankAccountConnect from '../components/Professional/BankAccountConnect';
import EarningsDashboard from '../components/Professional/EarningsDashboard';
import PayoutHistory from '../components/Professional/PayoutHistory';
import GoogleCalendarConnect from '../components/Professional/GoogleCalendarConnect';

const EarningsPage = () => {
  const { user: contextUser, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [localUser, setLocalUser] = useState(null);

  // Fallback: Read user directly from localStorage if context doesn't have it
  React.useEffect(() => {
    const userData = localStorage.getItem('userData');
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const token = localStorage.getItem('token');

    console.log('🔍 [EarningsPage] localStorage Debug:', {
      hasUserData: !!userData,
      userDataRaw: userData?.substring(0, 100),
      userDataParsed: userData ? JSON.parse(userData) : null,
      isLoggedIn,
      hasToken: !!token
    });

    // If context user is undefined but localStorage has user data, use localStorage
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setLocalUser(parsedUser);
      console.log('✅ [EarningsPage] Loaded user from localStorage:', parsedUser);
    }
  }, []);

  // Use context user if available, otherwise fall back to localStorage user
  const user = contextUser || localUser;

  console.log('🔍 [EarningsPage] User from context:', contextUser);
  console.log('🔍 [EarningsPage] User from localStorage:', localUser);
  console.log('🔍 [EarningsPage] Final user:', user);
  console.log('🔍 [EarningsPage] Loading:', loading);
  console.log('🔍 [EarningsPage] userType:', user?.userType, 'role:', user?.role);

  // Redirect if not a professional - use useEffect to avoid React Router warning
  React.useEffect(() => {
    if (!loading && user) {
      const isProfessional = user.userType === 'professional' || user.role === 'professional';
      if (!isProfessional) {
        console.log('❌ [EarningsPage] User is not a professional, redirecting...');
        navigate('/dashboard');
      } else {
        console.log('✅ [EarningsPage] User is a professional, showing page');
      }
    }
  }, [user, loading, navigate]);

  // Show loading while auth is being checked AND we haven't loaded from localStorage yet
  if (loading && !localUser) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show nothing while checking user type (after loading)
  if (!user || (user.userType !== 'professional' && user.role !== 'professional')) {
    console.log('❌ [EarningsPage] Returning null - no user or not professional');
    return null;
  }

  console.log('✅ [EarningsPage] Rendering earnings page for professional');

  const tabs = [
    { id: 'dashboard', label: 'Earnings', icon: '💰' },
    { id: 'history', label: 'Payout History', icon: '📜' },
    { id: 'bank', label: 'Bank Account', icon: '🏦' }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-4 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            Earnings & Payouts
          </h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Manage your earnings, view payout history, and connect your bank account
          </p>
        </div>

        {/* Tab Navigation - Dropdown on mobile, tabs on desktop */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
          {/* Mobile Dropdown */}
          <div className="md:hidden p-4">
            <label htmlFor="tab-select" className="sr-only">Select a tab</label>
            <select
              id="tab-select"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-2 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500"
            >
              {tabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.icon} {tab.label}
                </option>
              ))}
            </select>
          </div>

          {/* Desktop Tabs */}
          <div className="hidden md:block border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm transition-colors
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'dashboard' && <EarningsDashboard />}
          {activeTab === 'history' && <PayoutHistory />}
          {activeTab === 'bank' && <BankAccountConnect />}
        </div>
      </div>
    </div>
  );
};

export default EarningsPage;
